import { useEffect, useRef, useCallback } from 'react';

const CACHE_KEY = 'profilesCache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const SCROLL_KEY = 'profilesScrollPosition';
const FILTER_KEY = 'profilesFilters';

/**
 * Production-level caching hook for profiles
 * Handles:
 * - Data caching with TTL
 * - Scroll position persistence
 * - Filter state persistence
 * - Memory management
 */
export function useProfilesCache() {
    const cacheRef = useRef(null);

    const getCache = useCallback(() => {
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (!cached) return null;

            const parsed = JSON.parse(cached);
            const now = Date.now();

            // Check if cache is still valid
            if (now - parsed.timestamp > CACHE_DURATION) {
                localStorage.removeItem(CACHE_KEY);
                return null;
            }

            return parsed.data;
        } catch (error) {
            console.error('Error reading profile cache:', error);
            localStorage.removeItem(CACHE_KEY);
            return null;
        }
    }, []);

    const setCache = useCallback((data) => {
        try {
            localStorage.setItem(CACHE_KEY, JSON.stringify({
                data,
                timestamp: Date.now()
            }));
        } catch (error) {
            console.error('Error setting profile cache:', error);
        }
    }, []);

    const clearCache = useCallback(() => {
        try {
            localStorage.removeItem(CACHE_KEY);
        } catch (error) {
            console.error('Error clearing profile cache:', error);
        }
    }, []);

    const saveScrollPosition = useCallback((position) => {
        try {
            sessionStorage.setItem(SCROLL_KEY, position.toString());
        } catch (error) {
            console.error('Error saving scroll position:', error);
        }
    }, []);

    const getScrollPosition = useCallback(() => {
        try {
            const position = sessionStorage.getItem(SCROLL_KEY);
            return position ? parseInt(position, 10) : 0;
        } catch (error) {
            console.error('Error getting scroll position:', error);
            return 0;
        }
    }, []);

    const clearScrollPosition = useCallback(() => {
        try {
            sessionStorage.removeItem(SCROLL_KEY);
        } catch (error) {
            console.error('Error clearing scroll position:', error);
        }
    }, []);

    const saveFilters = useCallback((filters) => {
        try {
            sessionStorage.setItem(FILTER_KEY, JSON.stringify(filters));
        } catch (error) {
            console.error('Error saving filters:', error);
        }
    }, []);

    const getFilters = useCallback(() => {
        try {
            const filters = sessionStorage.getItem(FILTER_KEY);
            return filters ? JSON.parse(filters) : null;
        } catch (error) {
            console.error('Error getting filters:', error);
            return null;
        }
    }, []);

    const clearFilters = useCallback(() => {
        try {
            sessionStorage.removeItem(FILTER_KEY);
        } catch (error) {
            console.error('Error clearing filters:', error);
        }
    }, []);

    return {
        // Cache management
        getCache,
        setCache,
        clearCache,

        // Scroll management
        saveScrollPosition,
        getScrollPosition,
        clearScrollPosition,

        // Filter management
        saveFilters,
        getFilters,
        clearFilters
    };
}

/**
 * Hook to manage scroll position restoration
 * Production-ready scroll restoration for Next.js App Router
 */
export function useScrollRestoration(containerRef) {
    const scrollRestoredRef = useRef(false);
    const scrollTimersRef = useRef([]);

    useEffect(() => {
        let mounted = true;

        // Check if we're coming back from a profile page
        const comingFromProfile = sessionStorage.getItem('comingFromProfiles') === 'true';

        // Only clear if not coming from profile
        if (!comingFromProfile) {
            // Clear saved position if not coming from profile page
            sessionStorage.removeItem('profilesScrollPosition');
        }

        // Handle page show event (back/forward navigation)
        const handlePageShow = (event) => {
            // Reset flag if page is loaded from cache
            if (event.persisted) {
                scrollRestoredRef.current = false;
            }
            // Note: Scroll restoration is now handled in the component after data load
        };

        window.addEventListener('pageshow', handlePageShow);

        return () => {
            mounted = false;
            window.removeEventListener('pageshow', handlePageShow);
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            // Don't save scroll position if we're currently restoring or navigating to profile
            const isRestoring = sessionStorage.getItem('isRestoringScroll') === 'true';
            const isNavigating = sessionStorage.getItem('navigatingToProfile') === 'true';
            if (isRestoring || isNavigating) {
                return;
            }
            try {
                const scrollPos = window.scrollY.toString();
                sessionStorage.setItem('profilesScrollPosition', scrollPos);
            } catch (error) {
                console.error('Error saving scroll position:', error);
            }
        };

        // Throttle scroll events for better performance
        let ticking = false;
        const throttledScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', throttledScroll, { passive: true });
        return () => window.removeEventListener('scroll', throttledScroll);
    }, []);
}

/**
 * Hook for individual profile caching
 */
export function useProfileCache() {
    const getCachedProfile = useCallback((userId) => {
        try {
            const cacheKey = `profile_${userId}`;
            const cached = sessionStorage.getItem(cacheKey);
            if (!cached) return null;

            const parsed = JSON.parse(cached);
            const now = Date.now();

            // Cache for 10 minutes in session
            if (now - parsed.timestamp > 10 * 60 * 1000) {
                sessionStorage.removeItem(cacheKey);
                return null;
            }

            return parsed.data;
        } catch (error) {
            console.error('Error reading profile cache:', error);
            return null;
        }
    }, []);

    const setCachedProfile = useCallback((userId, data) => {
        try {
            const cacheKey = `profile_${userId}`;
            sessionStorage.setItem(cacheKey, JSON.stringify({
                data,
                timestamp: Date.now()
            }));
        } catch (error) {
            console.error('Error caching profile:', error);
        }
    }, []);

    const clearProfileCache = useCallback((userId) => {
        try {
            const cacheKey = `profile_${userId}`;
            sessionStorage.removeItem(cacheKey);
        } catch (error) {
            console.error('Error clearing profile cache:', error);
        }
    }, []);

    return {
        getCachedProfile,
        setCachedProfile,
        clearProfileCache
    };
}

